import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../shared/hooks/useAxios';
import AppDataTable from '../../shared/components/AppDataTable/AppDataTable';
import SpinnerOverlay from '../../shared/components/SpinnerOverlay/SpinnerOverlay';
import { usePermission } from '../../shared/hooks/usePermission';
import { useCurrentUser } from '../../auth/auth.store';
import { STATUS_FALLBACK, STATUS_STYLE } from '../../shared/utils/statusStyles';
import { PERMISSIONS } from '../../auth/permissions';
import { handleError } from '../../shared/utils';
import SavedFiltersDialog from './SavedFiltersDialog';
import './Dashboard.scss';
import '../requests/RequestList.scss';
import FileIcon from '../../assets/icons/file-icon.svg';
import InfoIcon from '../../assets/icons/info-icon.svg';
import CheckIcon from '../../assets/icons/check-icon.svg';
import RocketIcon from '../../assets/icons/rocket-icon.svg';
import ReviewIcon from '../../assets/icons/review-icon.svg';
import FilterIcon from '../../assets/icons/filter-icon.svg';
const DASHBOARD_ENDPOINT = 'workflow-manager/dashboard';
const REQUESTS_ENDPOINT = 'workflow-manager/requests';
const DASHBOARD_PAGE_SIZE = 10;
const MY_REVIEWS_PAGE_SIZE = 10;
const REQUEST_SORT_FIELDS = {
    requestId: 'request_number',
    requestType: 'request_type',
    title: 'title',
    country: 'country',
    creativeTeam: 'creative_team',
    category: 'category',
    status: 'status',
    createdBy: 'created_by',
    createdDate: 'created_at',
};
const CATEGORY_STYLE = {
    'Packaging & Labeling': { background: '#DBEAFE', color: '#1447E6' },
    'Digital Content': { background: '#DBDEFE', color: '#4C14E6' },
    'Social Media': { background: '#F3FEDB', color: '#5A8104' },
    'Product Details': { background: '#FEF2DB', color: '#A97005' },
    'Claims Review': { background: '#FEEBFA', color: '#B40990' },
    'Retailer-Specific Content': { background: '#DBFBFE', color: '#048490' },
    'Product Launch': { background: '#F1FEDB', color: '#3C5E03' },
    'Content Update': { background: '#FEDBEB', color: '#A4054D' },
    'Asset Reuse': { background: '#DEFEDB', color: '#0C6803' },
    'AI Playground Validation': { background: '#F9DBFE', color: '#730486' },
};
const timePeriodOptions = [
    { name: 'Last 7 days', value: 7 },
    { name: 'Last 30 days', value: 30 },
    { name: 'Last 60 days', value: 60 },
    { name: 'Last 90 days', value: 90 },
];
const SAVED_FILTER_QUERY_KEYS = {
    status: 'status_id',
    status_id: 'status_id',
    country: 'country_id',
    country_id: 'country_id',
    category: 'request_category_id',
    category_id: 'request_category_id',
    request_category: 'request_category_id',
    request_category_id: 'request_category_id',
    creative_team: 'creative_team',
    created_by: 'created_by',
    request_type: 'request_type',
    request_type_id: 'request_type_id',
    created_date: 'created_date',
};
const quickActionDefinitions = [
    { label: 'Start Request', apiLabels: ['Start Request', 'Create Request'], icon: RocketIcon, bg: '#FAF5FF', route: '/requests/create' },
    // { label: 'Pre Check Compliance', apiLabels: ['Pre Check Compliance'], icon: PreCheckIcon, bg: '#F0FDF4', route: '/playground' },
    { label: 'My Reviews', apiLabels: ['My Reviews'], icon: ReviewIcon, bg: '#F6F8FD', route: '/workflows' },
    { label: 'Saved Filters', apiLabels: ['Saved Filters'], icon: FilterIcon, bg: '#FFFEF5', route: '/workflows' },
];
const chartColors = ['#1C398E', '#4FC3F7', '#F1B434', '#E87722', '#4A7729', '#512D6D', '#BDD6E6', '#22C55E', '#86EFAC', '#38BDF8', '#FCA5A5', '#94A3B8'];
const STATUS_CHART_COLORS = {
    'Approved with Changes': '#1C398E',
    Rejected: '#4FC3F7',
    Approved: '#F1B434',
    'Revise and Resubmit': '#E87722',
    'In Progress': '#4A7729',
    Draft: '#512D6D',
    'Needs Meeting': '#BDD6E6',
    Completed: '#22C55E',
    'Design In Progress': '#0EA5E9',
    'Design Completed': '#86EFAC',
    'Review In Progress': '#8B5CF6',
    Cancelled: '#FCA5A5',
    Hold: '#94A3B8',
    Deleted: '#64748B',
};
const DONUT_LEGEND_PAGE_SIZE = 5;
const REVIEWER_GROUP_COLORS = {
    MKTG: '#1C398E',
    SA: '#4FC3F7',
    RA: '#BDD6E6',
    LEGAL: '#F1B434',
    MA: '#E87722',
};
function reviewerGroupColor(label, index) {
    return REVIEWER_GROUP_COLORS[label] ?? chartColors[index % chartColors.length];
}
function payloadOf(response) {
    return response?.data?.data ?? response?.data?.messages?.[0] ?? response?.data ?? {};
}
function displayName(value) {
    if (value == null)
        return '';
    if (typeof value === 'object')
        return value.name ?? value.label ?? value.title ?? '';
    return String(value);
}
function formatDate(value) {
    if (!value)
        return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return String(value);
    return new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(date);
}
// design_due_at / review_due_at are calendar dates (the same values Review &
// Submit stores as bare "YYYY-MM-DD"), not real timestamps — `new Date(value)`
// parses them as UTC midnight, which renders as the previous day in any
// negative-offset (e.g. US) browser timezone. Parse the date parts directly
// instead, using whatever the API sent regardless of whether it included a
// time component.
function formatDueDate(value) {
    if (!value)
        return '';
    const iso = String(value);
    const date = new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
    if (Number.isNaN(date.getTime()))
        return String(value);
    return new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(date);
}
function initialsOf(name) {
    const words = name.trim().split(/\s+/).filter(Boolean);
    return words.length ? `${words[0][0]}${words[1]?.[0] ?? ''}`.toUpperCase() : '—';
}
function toIdArray(value) {
    if (value == null || value === '')
        return [];
    return (Array.isArray(value) ? value : [value])
        .map(item => item != null && typeof item === 'object' ? String(item.id ?? item.value ?? '') : String(item))
        .filter(Boolean);
}
// Colors for statuses missing from STATUS_CHART_COLORS — none overlap the mapped ones.
const STATUS_FALLBACK_COLORS = ['#EC4899', '#14B8A6', '#6366F1', '#A3E635', '#D946EF', '#B45309', '#F43F5E', '#0F766E', '#7C3AED', '#CA8A04', '#9F1239', '#065F46'];
const normalizeStatus = (status) => status.trim().toLowerCase().replace(/\s+/g, ' ');
const STATUS_CHART_COLORS_NORMALIZED = Object.fromEntries(Object.entries(STATUS_CHART_COLORS).map(([status, color]) => [normalizeStatus(status), color]));
// Assigns each status in the list a distinct color: its mapped color when available,
// otherwise the next unused fallback color (generated via golden-angle hue once exhausted).
function buildStatusColors(statuses) {
    const used = new Set();
    let fallbackIndex = 0;
    const nextFallback = () => {
        while (fallbackIndex < STATUS_FALLBACK_COLORS.length && used.has(STATUS_FALLBACK_COLORS[fallbackIndex]))
            fallbackIndex++;
        const color = STATUS_FALLBACK_COLORS[fallbackIndex]
            ?? `hsl(${Math.round((fallbackIndex * 137.508) % 360)}, 65%, 50%)`;
        fallbackIndex++;
        return color;
    };
    return statuses.map(status => {
        const mapped = STATUS_CHART_COLORS_NORMALIZED[normalizeStatus(status)];
        const color = mapped && !used.has(mapped) ? mapped : nextFallback();
        used.add(color);
        return color;
    });
}
function filterValues(value) {
    const entries = Array.isArray(value) ? value : [value];
    return entries.map(item => {
        if (typeof item === 'string')
            return item.trim();
        if (typeof item === 'number')
            return item;
        if (item && typeof item === 'object') {
            const option = item;
            return option.value ?? option.id ?? option.code ?? option.key;
        }
        return undefined;
    }).filter((item) => ((typeof item === 'string' && item !== '' && item.toLowerCase() !== 'null' && item.toLowerCase() !== 'undefined')
        || typeof item === 'number'));
}
function appendSavedFilterParams(query, filter) {
    const appendedKeys = new Set();
    Object.entries(filter.query_params ?? {}).forEach(([key, rawValue]) => {
        const queryKey = SAVED_FILTER_QUERY_KEYS[key] ?? key;
        filterValues(rawValue).forEach(value => query.append(queryKey, String(value)));
        appendedKeys.add(queryKey);
    });
    (filter.criteria ?? []).forEach(criterion => {
        const queryKey = SAVED_FILTER_QUERY_KEYS[criterion.field_key];
        if (!queryKey || appendedKeys.has(queryKey))
            return;
        const rawValue = criterion.values
            ?? criterion.field_values
            ?? criterion.selected_values
            ?? criterion.field_value
            ?? criterion.selected_value
            ?? criterion.value;
        filterValues(rawValue).forEach(value => query.append(queryKey, String(value)));
    });
}
function truncatedList(names) {
    if (!names.length)
        return '—';
    if (names.length <= 3)
        return names.join(', ');
    return (_jsxs("span", { className: "rl-truncated-list", "data-pr-tooltip": names.join(', '), "data-pr-position": "top", children: [names.slice(0, 3).join(', '), "\u2026", _jsxs("span", { className: "rl-more-badge", children: ["+", names.length - 3, " more"] })] }));
}
function mapRequest(row) {
    const creator = row.created_by_name ?? displayName(row.created_by);
    const countryIds = toIdArray(row.country_ids ?? row.country_id ?? row.country);
    return {
        id: String(row.id ?? row.request_id ?? row.uuid ?? row.request_number ?? ''),
        requestId: String(row.request_number ?? row.requestNumber ?? ''),
        requestType: row.request_type_name ?? displayName(row.request_type),
        title: row.title ?? '',
        country: countryIds.join(', ') || row.country_name || displayName(row.country),
        scope: String(row.scope ?? '').trim().toLowerCase(),
        creativeTeam: toIdArray(row.creative_team),
        category: row.category_name || displayName(row.category),
        status: row.status_name ?? displayName(row.status),
        statusId: String(row.status_id ?? row.status?.id ?? ''),
        createdBy: creator,
        createdByInitials: initialsOf(creator),
        createdDate: formatDate(row.created_at ?? row.created_date ?? row.created_datetime),
        designDueDate: formatDueDate(row.design_due_at),
        reviewDueDate: formatDueDate(row.review_due_at),
    };
}
export default function DashboardPage() {
    const [timePeriodValue, setTimePeriodValue] = useState(30);
    const [dashboard, setDashboard] = useState({});
    const [requests, setRequests] = useState([]);
    const [totalRequests, setTotalRequests] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState(null);
    // const [creativeTeamNames, setCreativeTeamNames] = useState<Record<string, string>>({});
    const [countryNames, setCountryNames] = useState({});
    const [productCategoryOptions, setProductCategoryOptions] = useState([]);
    const [selectedProductCategory, setSelectedProductCategory] = useState('');
    const [savedFiltersVisible, setSavedFiltersVisible] = useState(false);
    const [activeSavedFilter, setActiveSavedFilter] = useState(null);
    const [defaultSavedFilter, setDefaultSavedFilter] = useState(null);
    const [defaultFilterEnabled, setDefaultFilterEnabled] = useState(true);
    const [reviewScope, setReviewScope] = useState('default');
    const [drillDown, setDrillDown] = useState(null);
    const [statusIdsByName, setStatusIdsByName] = useState({});
    const [donutPage, setDonutPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const can = usePermission();
    const currentUser = useCurrentUser();
    const { get, post } = useAxios();
    const errorToast = useRef(null);
    const listTooltipRef = useRef(null);
    const reviewerGroupChartRef = useRef(null);
    useEffect(() => {
        listTooltipRef.current?.updateTargetEvents();
    }, [requests]);
    useEffect(() => {
        // get('workflow-manager/config/dropdowns?fields=creative_team', { skipSpinner: true })
        //     .then(response => {
        //         const data = response?.data?.data ?? {};
        //         const teams: any[] = data.creative_team ?? [];
        //         setCreativeTeamNames(Object.fromEntries(teams.map(item => [String(item.id), item.label ?? item.name ?? String(item.id)])));
        //     })
        //     .catch(() => setCreativeTeamNames({}));
        get('user-manager/master/countries', { skipSpinner: true })
            .then(response => {
            const countries = response?.data?.messages?.[0] ?? [];
            setCountryNames(Object.fromEntries(countries.map(item => [String(item.code), item.name ?? item.code])));
        })
            .catch(() => setCountryNames({}));
        get('workflow-manager/config/dropdowns?fields=product_category,status', { skipSpinner: true })
            .then(response => {
            const data = response?.data?.data ?? {};
            const categories = data.product_category ?? [];
            setProductCategoryOptions(categories.map(item => ({
                label: item.label ?? item.name ?? String(item.id),
                value: String(item.id),
            })));
            // Requests are filtered by request-level status ids; content-level statuses
            // reuse the same labels with different ids, so they are excluded.
            const statuses = (data.status ?? []).filter((item) => item.level === 'request');
            setStatusIdsByName(Object.fromEntries(statuses.map(item => [
                String(item.label ?? item.name ?? '').toLowerCase(),
                String(item.id),
            ])));
        })
            .catch(() => {
            setProductCategoryOptions([]);
            setStatusIdsByName({});
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        let active = true;
        const loadDashboard = async () => {
            setLoading(true);
            setError('');
            try {
                const query = new URLSearchParams({ days: String(timePeriodValue) });
                if (selectedProductCategory)
                    query.set('product_category_id', selectedProductCategory);
                const dashboardResponse = await get(`${DASHBOARD_ENDPOINT}?${query.toString()}`, { skipSpinner: true });
                if (!active)
                    return;
                const dashboardPayload = payloadOf(dashboardResponse);
                const defaults = (dashboardPayload.saved_filters ?? []).filter(filter => filter.is_default);
                if (defaults.length > 1) {
                    console.warn('Dashboard returned more than one default saved filter. Using the first filter.');
                }
                const dashboardDefaultFilter = defaults[0] ?? null;
                const effectiveSavedFilter = activeSavedFilter
                    ?? (reviewScope === 'default' && defaultFilterEnabled && !drillDown ? dashboardDefaultFilter : null);
                const requestQuery = new URLSearchParams({
                    days: String(timePeriodValue),
                    page: String(currentPage),
                    // "My Reviews" is fetched at a fixed page_size of 20,
                    // independent of the dashboard table's own page size.
                    page_size: String(reviewScope === 'my_reviews' ? MY_REVIEWS_PAGE_SIZE : DASHBOARD_PAGE_SIZE),
                });
                if (sortField && sortOrder) {
                    requestQuery.set('sort_by', REQUEST_SORT_FIELDS[sortField] ?? sortField);
                    requestQuery.set('sort_order', sortOrder === 1 ? 'asc' : 'desc');
                }
                if (reviewScope === 'my_reviews')
                    requestQuery.set('my_reviews', 'true');
                if (reviewScope === 'all_reviews')
                    requestQuery.set('all_reviews', 'true');
                if (effectiveSavedFilter)
                    appendSavedFilterParams(requestQuery, effectiveSavedFilter);
                if (drillDown?.statusId)
                    requestQuery.set('status_id', drillDown.statusId);
                if (drillDown?.isOverdue)
                    requestQuery.set('is_overdue', 'true');
                if (drillDown?.departmentId != null) {
                    requestQuery.set('department_id', String(drillDown.departmentId));
                    if (selectedProductCategory)
                        requestQuery.set('product_category_id', selectedProductCategory);
                }
                const requestsResponse = await get(`${REQUESTS_ENDPOINT}?${requestQuery.toString()}`, { skipSpinner: true });
                if (!active)
                    return;
                setDashboard(dashboardPayload);
                setDefaultSavedFilter(dashboardDefaultFilter);
                const requestPayload = payloadOf(requestsResponse);
                const rows = Array.isArray(requestPayload)
                    ? requestPayload
                    : (requestPayload.requests ?? requestPayload.items ?? []);
                setRequests(rows.map(mapRequest));
                setTotalRequests(requestsResponse?.data?.total
                    ?? requestPayload.total
                    ?? requestPayload.total_count
                    ?? rows.length);
            }
            catch {
                if (active) {
                    setDashboard({});
                    setRequests([]);
                    setTotalRequests(0);
                    setError('Unable to load dashboard data. Please try again.');
                }
            }
            finally {
                if (active)
                    setLoading(false);
            }
        };
        loadDashboard();
        return () => { active = false; };
        // `get` is intentionally omitted because the hook returns a new function each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timePeriodValue, currentPage, sortField, sortOrder, reviewScope, activeSavedFilter, defaultFilterEnabled, selectedProductCategory, drillDown]);
    const selectedSavedFilter = activeSavedFilter
        ?? (reviewScope === 'default' && defaultFilterEnabled && !drillDown ? defaultSavedFilter : null);
    const applyDrillDown = (next) => {
        setActiveSavedFilter(null);
        setReviewScope('default');
        // "Total Requests" (next = null) means everything, so the default saved filter is dropped too.
        if (!next)
            setDefaultFilterEnabled(false);
        setDrillDown(next);
        setCurrentPage(1);
    };
    const statusIdOf = (status, statusId) => (statusId != null && statusId !== '' ? String(statusId) : statusIdsByName[status.toLowerCase()]);
    const applyStatusDrillDown = (status, statusId) => {
        const id = statusIdOf(status, statusId);
        if (!id)
            return;
        applyDrillDown({ label: status, statusId: id });
    };
    const statCards = [
        { label: 'Total Requests', value: dashboard.total_projects?.count ?? 0, visible: dashboard.total_projects?.is_visible !== false, icon: FileIcon, iconBg: '#BDD6E6', drillDown: null },
        { label: 'Overdue Requests', value: dashboard.overdue_projects?.count ?? 0, visible: dashboard.overdue_projects?.is_visible !== false, icon: InfoIcon, iconBg: '#F1B434', drillDown: { label: 'Overdue', isOverdue: true } },
        { label: 'Completed Requests', value: dashboard.completed_projects?.count ?? 0, visible: dashboard.completed_projects?.is_visible !== false, icon: CheckIcon, iconBg: '#4A7729', drillDown: statusIdsByName.completed ? { label: 'Completed', statusId: statusIdsByName.completed } : undefined },
    ].filter(card => card.visible);
    const visibleQuickActions = quickActionDefinitions.filter(action => {
        if (action.route === '/requests/create' && !can(PERMISSIONS.REQUEST_CREATE))
            return false;
        if (!dashboard.quick_actions)
            return true;
        const apiAction = dashboard.quick_actions.find(item => action.apiLabels.includes(item.label));
        return apiAction ? apiAction.is_visible !== false : true;
    });
    const reviewStatuses = dashboard.review_status_chart ?? [];
    const departmentRows = dashboard.overdue_by_reviewer_group?.departments ?? [];
    const reviewStatusTotal = reviewStatuses.reduce((total, item) => total + item.count, 0);
    const donutPageCount = Math.max(1, Math.ceil(reviewStatuses.length / DONUT_LEGEND_PAGE_SIZE));
    const donutPageStart = Math.min(donutPage, donutPageCount - 1) * DONUT_LEGEND_PAGE_SIZE;
    const pagedReviewStatuses = reviewStatuses.slice(donutPageStart, donutPageStart + DONUT_LEGEND_PAGE_SIZE);
    useEffect(() => {
        setDonutPage(0);
    }, [dashboard.review_status_chart]);
    const statusColors = useMemo(() => buildStatusColors(reviewStatuses.map(item => item.status)), [reviewStatuses]);
    const donutData = useMemo(() => ({
        labels: reviewStatuses.map(item => item.status),
        datasets: [{
                data: reviewStatuses.map(item => item.count),
                backgroundColor: statusColors,
                hoverBackgroundColor: statusColors,
                borderWidth: 2,
                borderColor: '#fff',
            }],
    }), [reviewStatuses, statusColors]);
    const donutOptions = {
        cutout: '65%',
        plugins: { legend: { display: false } },
        onClick: (_event, elements) => {
            const item = elements[0] ? reviewStatuses[elements[0].index] : undefined;
            if (item)
                applyStatusDrillDown(item.status, item.status_id);
        },
        onHover: (event, elements) => {
            const target = event.native?.target;
            if (target)
                target.style.cursor = elements.length ? 'pointer' : 'default';
        },
    };
    const reviewerGroupTotals = useMemo(() => departmentRows.map(row => ({
        departmentId: row.department_id,
        label: row.short_form || row.department_name,
        name: row.department_name,
        count: row.overdue_count ?? 0,
    })), [departmentRows]);
    const barData = useMemo(() => ({
        labels: reviewerGroupTotals.map(item => item.label),
        datasets: [{
                data: reviewerGroupTotals.map(item => item.count),
                backgroundColor: reviewerGroupTotals.map((item, index) => reviewerGroupColor(item.label, index)),
                borderRadius: 4,
                maxBarThickness: 56,
            }],
    }), [reviewerGroupTotals]);
    const barOptions = useMemo(() => ({
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false, axis: 'x' },
        hover: { mode: 'index', intersect: false, axis: 'x' },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items) => {
                        const index = items[0]?.dataIndex;
                        const group = index == null ? undefined : reviewerGroupTotals[index];
                        return group?.name || group?.label || '';
                    },
                },
            },
        },
        scales: {
            x: { grid: { display: false } },
            y: {
                beginAtZero: true,
                ticks: { precision: 0, stepSize: 1 },
                grid: { color: '#f1f5f9' },
            },
        },
    }), [reviewerGroupTotals]);
    const reviewerGroupTotal = dashboard.overdue_by_reviewer_group?.total_overdue
        ?? reviewerGroupTotals.reduce((total, item) => total + item.count, 0);
    const reviewerGroupChartWidth = Math.max(reviewerGroupTotals.length * 90, 0);
    // Chart.js only activates tooltips inside the plotted chartArea, which excludes the
    // x-axis tick label row — so hovering the category keys (MKTG, RA, ...) never triggers
    // the built-in tooltip on its own. We detect that zone manually and drive the tooltip
    // via Chart.js's programmatic setActiveElements API instead.
    useEffect(() => {
        const canvas = reviewerGroupChartRef.current?.getCanvas();
        if (!canvas)
            return undefined;
        const clearHighlight = () => {
            const chart = reviewerGroupChartRef.current?.getChart();
            if (!chart)
                return;
            chart.setActiveElements([]);
            chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
            chart.update('none');
        };
        const handleMouseMove = (event) => {
            const chart = reviewerGroupChartRef.current?.getChart();
            if (!chart?.chartArea)
                return;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const { bottom, left, right } = chart.chartArea;
            if (y <= bottom || x < left || x > right)
                return;
            const index = chart.scales.x.getValueForPixel(x);
            if (index == null || index < 0 || index >= reviewerGroupTotals.length) {
                clearHighlight();
                return;
            }
            chart.setActiveElements([{ datasetIndex: 0, index }]);
            chart.tooltip?.setActiveElements([{ datasetIndex: 0, index }], { x, y: bottom });
            chart.update('none');
        };
        // Clicking a bar (or its x-axis label) drills the Requests table down to
        // that department's overdue requests.
        const indexAtEvent = (event) => {
            const chart = reviewerGroupChartRef.current?.getChart();
            if (!chart?.chartArea)
                return null;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const { top, left, right } = chart.chartArea;
            if (y < top || x < left || x > right)
                return null;
            const index = chart.scales.x.getValueForPixel(x);
            return index == null || index < 0 || index >= reviewerGroupTotals.length ? null : index;
        };
        const handleClick = (event) => {
            const index = indexAtEvent(event);
            if (index == null)
                return;
            const group = reviewerGroupTotals[index];
            setActiveSavedFilter(null);
            setReviewScope('default');
            setDrillDown({ label: `Overdue · ${group.label}`, departmentId: group.departmentId, isOverdue: true });
            setCurrentPage(1);
        };
        const handlePointer = (event) => {
            canvas.style.cursor = indexAtEvent(event) == null ? 'default' : 'pointer';
        };
        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mousemove', handlePointer);
        canvas.addEventListener('mouseleave', clearHighlight);
        canvas.addEventListener('click', handleClick);
        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mousemove', handlePointer);
            canvas.removeEventListener('mouseleave', clearHighlight);
            canvas.removeEventListener('click', handleClick);
        };
    }, [reviewerGroupTotals]);
    const statusTemplate = (row) => row.status
        ? _jsx("span", { className: "rl-status-badge", style: STATUS_STYLE[row.status] ?? STATUS_FALLBACK, children: row.status })
        : '—';
    const categoryTemplate = (row) => row.category
        ? _jsx("span", { className: "rl-category-badge", style: CATEGORY_STYLE[row.category] ?? STATUS_FALLBACK, children: row.category })
        : '—';
    const avatarTemplate = (row) => _jsx("span", { className: "rl-avatar", title: row.createdBy, children: row.createdByInitials });
    const handleOpenRequest = async (row) => {
        if (row.status === 'Revision Requested') {
            try {
                await post(`${REQUESTS_ENDPOINT}/${row.id}/start-revision`);
            }
            catch (err) {
                handleError(err, errorToast);
                return;
            }
        }
        navigate(`/annotate/${row.id}/review`);
    };
    const requestIdTemplate = (row) => {
        const isRevisionRequested = row.status === 'Revision Requested';
        const canShowLink = isRevisionRequested
            ? can([
                PERMISSIONS.REQUEST_EDIT,
                PERMISSIONS.REQUEST_VIEW,
                PERMISSIONS.REQUEST_CREATE,
            ])
            : can([
                PERMISSIONS.REQUEST_EDIT,
                PERMISSIONS.REQUEST_VIEW,
                PERMISSIONS.REQUEST_CREATE,
            ]) && row.status?.toLowerCase() !== 'draft';
        if (!canShowLink)
            return _jsx("span", { children: row.requestId });
        return (_jsx("span", { className: "rl-request-type", role: "link", tabIndex: 0, onClick: () => handleOpenRequest(row), onKeyDown: (event) => event.key === 'Enter' && handleOpenRequest(row), children: row.requestId }));
    };
    // const creativeTeamTemplate = (row: DashboardRequest) => truncatedList(
    //     row.creativeTeam.map(id => creativeTeamNames[id] ?? id).filter(Boolean),
    // );
    const countryTemplate = (row) => {
        if (row.scope === 'global')
            return 'Global';
        return truncatedList(row.country.split(',').map(country => country.trim()).filter(Boolean).map(country => countryNames[country] ?? country));
    };
    const slaTemplate = (row) => {
        if (!row.designDueDate && !row.reviewDueDate)
            return '—';
        return _jsxs("div", { children: [row.designDueDate && _jsxs("div", { children: ["Design: ", row.designDueDate] }), row.reviewDueDate && _jsxs("div", { children: ["Review: ", row.reviewDueDate] })] });
    };
    const handlePageChange = (event) => setCurrentPage((event.page ?? 0) + 1);
    const handleSort = (event) => {
        setSortField(String(event.sortField ?? ''));
        setSortOrder(event.sortOrder ?? null);
        setCurrentPage(1);
    };
    const showMyReviews = () => {
        setDrillDown(null);
        setActiveSavedFilter(null);
        setDefaultFilterEnabled(false);
        setReviewScope('my_reviews');
        setCurrentPage(1);
    };
    const showAllReviews = () => {
        setDrillDown(null);
        setActiveSavedFilter(null);
        setDefaultFilterEnabled(false);
        setReviewScope('all_reviews');
        setCurrentPage(1);
    };
    const applySavedFilter = (filter) => {
        setDrillDown(null);
        setActiveSavedFilter(filter);
        setReviewScope('default');
        setCurrentPage(1);
        setSavedFiltersVisible(false);
    };
    const clearSavedFilter = () => {
        setActiveSavedFilter(null);
        setDefaultFilterEnabled(false);
        setReviewScope('default');
        setCurrentPage(1);
    };
    const handleSavedFilterUpdated = (filter) => {
        setActiveSavedFilter(current => current?.id === filter.id ? filter : current);
        setDefaultSavedFilter(current => current?.id === filter.id ? filter : current);
        setCurrentPage(1);
    };
    const handleSavedFilterDeleted = (filterId) => {
        if (defaultSavedFilter?.id === filterId) {
            setDefaultSavedFilter(null);
            setDefaultFilterEnabled(false);
        }
        if (activeSavedFilter?.id === filterId) {
            setActiveSavedFilter(null);
            setCurrentPage(1);
        }
    };
    return (_jsxs("div", { className: "dashboard", children: [_jsx(Toast, { ref: errorToast, position: "bottom-right" }), _jsxs("div", { className: "flex justify-content-between align-items-center mb-4", children: [_jsx("h2", { className: "m-0", style: { color: '#1C398E' }, children: "Dashboard" }), _jsx(Dropdown, { value: timePeriodValue, options: timePeriodOptions, onChange: (e) => {
                            setTimePeriodValue(e.value);
                            setCurrentPage(1);
                        }, optionLabel: "name", optionValue: "value", appendTo: "self" })] }), error && _jsx("div", { className: "p-message p-message-error mb-3", role: "alert", children: _jsx("span", { className: "p-message-text", children: error }) }), _jsxs("div", { className: "dashboard-top-grid", children: [_jsx("div", { className: "stat-cards-column", children: statCards.map(card => (_jsxs(Card, { className: `stat-card${card.drillDown !== undefined ? ' stat-card-clickable' : ''}`, onClick: card.drillDown !== undefined ? () => applyDrillDown(card.drillDown ?? null) : undefined, children: [_jsx("div", { className: "stat-value", style: { color: '#1C398E' }, children: card.value.toLocaleString() }), _jsx("div", { className: "stat-label", children: card.label }), _jsx("div", { className: "stat-icon", style: { background: card.iconBg }, children: _jsx("img", { src: card.icon, alt: card.label, width: 16, height: 16 }) })] }, card.label))) }), _jsxs("div", { className: "chart-card", children: [_jsxs("div", { className: "chart-card-header", children: [_jsx("h3", { className: "chart-title", children: "Status Overview" }), _jsxs("span", { className: "chart-total", children: [reviewStatusTotal, " total Requests"] })] }), _jsxs("div", { className: "donut-chart-body", children: [_jsxs("div", { className: "donut-chart-visual", children: [_jsx(Chart, { type: "doughnut", data: donutData, options: donutOptions }), _jsxs("div", { className: "donut-center-total", children: [_jsx("span", { children: "Total" }), _jsx("strong", { children: reviewStatusTotal })] })] }), _jsxs("div", { className: "donut-legend-section", children: [_jsxs("div", { className: "donut-legend-pager", children: [_jsx("span", { children: reviewStatuses.length ? `${donutPageStart + 1}-${Math.min(donutPageStart + DONUT_LEGEND_PAGE_SIZE, reviewStatuses.length)} OF ${reviewStatuses.length}` : '0 OF 0' }), _jsx("span", { className: "donut-legend-track", children: Array.from({ length: donutPageCount }, (_, page) => (_jsx("span", { className: `donut-legend-dot-page${page === donutPage ? ' active' : ''}` }, page))) })] }), _jsx("div", { className: "donut-legend", children: pagedReviewStatuses.map((item, index) => {
                                                    const color = statusColors[donutPageStart + index];
                                                    const clickable = !!statusIdOf(item.status, item.status_id);
                                                    return (_jsxs("div", { className: `donut-legend-item${clickable ? ' donut-legend-item-clickable' : ''}${drillDown?.label === item.status ? ' active' : ''}`, onClick: clickable ? () => applyStatusDrillDown(item.status, item.status_id) : undefined, children: [_jsx("span", { className: "donut-legend-dot", style: { background: color } }), _jsx("span", { className: "donut-legend-label", children: item.status }), _jsx("span", { className: "donut-legend-value", style: { color }, children: item.count })] }, item.status));
                                                }) }), _jsxs("div", { className: "donut-legend-pager-nav", children: [_jsxs("button", { type: "button", disabled: donutPage <= 0, onClick: () => setDonutPage(page => Math.max(0, page - 1)), children: [_jsx("i", { className: "pi pi-chevron-left" }), " Prev"] }), _jsxs("button", { type: "button", disabled: donutPage >= donutPageCount - 1, onClick: () => setDonutPage(page => Math.min(donutPageCount - 1, page + 1)), children: ["Next ", _jsx("i", { className: "pi pi-chevron-right" })] })] })] })] })] }), _jsxs("div", { className: "chart-card reviewer-group-card", children: [_jsxs("div", { className: "chart-card-header", children: [_jsx("h3", { className: "chart-title", children: "Overdue Requests by Reviewer Group" }), _jsxs("span", { className: "chart-total", children: ["Total: ", _jsx("strong", { children: reviewerGroupTotal })] })] }), _jsx(Dropdown, { value: selectedProductCategory, options: productCategoryOptions, onChange: (e) => setSelectedProductCategory(e.value), optionLabel: "label", optionValue: "value", placeholder: "Product Category", showClear: !!selectedProductCategory, className: "chart-category-dropdown", appendTo: "self" }), _jsx("div", { className: "reviewer-group-chart-scroll", children: _jsx("div", { className: "reviewer-group-chart-inner", style: { width: `${reviewerGroupChartWidth}px` }, children: _jsx(Chart, { ref: reviewerGroupChartRef, type: "bar", data: barData, options: barOptions, style: { width: '100%', height: '100%' } }) }) })] })] }), _jsxs("div", { className: "quick-actions-section", children: [_jsx("h3", { className: "quick-actions-title", children: "Quick Actions" }), _jsx("div", { className: "quick-actions-list", children: visibleQuickActions.map(action => (_jsxs("button", { className: "quick-action-btn", style: { background: action.bg }, onClick: () => {
                                if (action.label === 'Saved Filters')
                                    setSavedFiltersVisible(true);
                                else if (action.label === 'My Reviews')
                                    showMyReviews();
                                else
                                    navigate(action.route);
                            }, children: [_jsx("img", { src: action.icon, alt: action.label, width: 20, height: 20 }), _jsx("span", { children: action.label })] }, action.label))) })] }), _jsxs("div", { className: "projects-section", children: [_jsxs("div", { className: "projects-header", children: [_jsx("h3", { className: "projects-title", children: "Requests" }), _jsxs("div", { className: "projects-filter-btns", children: [drillDown && (_jsxs("button", { className: "filter-btn active", onClick: () => setCurrentPage(1), children: [drillDown.label, " ", _jsx("span", { className: "filter-count", children: totalRequests })] })), !drillDown && !selectedSavedFilter && (reviewScope === 'default' || reviewScope === 'all_reviews') && (_jsxs("button", { className: "filter-btn active", onClick: () => setCurrentPage(1), children: ["All ", _jsx("span", { className: "filter-count", children: totalRequests })] })), (drillDown || reviewScope === 'my_reviews' || selectedSavedFilter) && (_jsx("button", { className: "filter-btn", onClick: () => {
                                            if (drillDown) {
                                                setDrillDown(null);
                                                setCurrentPage(1);
                                            }
                                            else if (selectedSavedFilter)
                                                clearSavedFilter();
                                            else
                                                showAllReviews();
                                        }, children: "Clear Filter" }))] })] }), _jsx("div", { className: "request-list dashboard-request-table", children: _jsxs("div", { className: "rl-card", children: [loading && _jsx(SpinnerOverlay, {}), _jsx(Tooltip, { ref: listTooltipRef, target: ".dashboard-request-table .rl-truncated-list", className: "rl-list-tooltip", showDelay: 150 }), _jsxs(AppDataTable, { value: requests, totalLabel: "Requests", rows: DASHBOARD_PAGE_SIZE, lazy: true, totalRecords: totalRequests, first: (currentPage - 1) * DASHBOARD_PAGE_SIZE, onPage: handlePageChange, sortField: sortField, sortOrder: sortOrder, onSort: handleSort, scrollable: true, emptyMessage: "No requests found for the selected period.", tableStyle: { minWidth: '70rem' }, children: [_jsx(Column, { field: "requestId", header: "REQUEST ID", body: requestIdTemplate, sortable: true, frozen: true, style: { minWidth: '140px' } }), _jsx(Column, { field: "requestType", header: "REQUEST TYPE", sortable: true, style: { minWidth: '170px' } }), _jsx(Column, { field: "title", header: "TITLE", body: (row) => row.title || '—', sortable: true, style: { minWidth: '150px' } }), _jsx(Column, { field: "country", header: "COUNTRY", body: countryTemplate, sortable: true, style: { minWidth: '110px' } }), _jsx(Column, { field: "status", header: "STATUS", body: statusTemplate, sortable: true, style: { minWidth: '120px' } }), _jsx(Column, { field: "designDueDate", header: "SLA TIMING", body: slaTemplate, style: { minWidth: '160px' } }), _jsx(Column, { field: "createdBy", header: "CREATED BY", body: avatarTemplate, sortable: true, style: { minWidth: '120px' } }), _jsx(Column, { field: "createdDate", header: "CREATED DATE", sortable: true, body: (row) => row.createdDate || '—', style: { minWidth: '140px' } })] })] }) })] }), _jsx(SavedFiltersDialog, { visible: savedFiltersVisible, onHide: () => setSavedFiltersVisible(false), selectedFilterId: selectedSavedFilter?.id, onSelectFilter: applySavedFilter, onFilterUpdated: handleSavedFilterUpdated, onFilterDeleted: handleSavedFilterDeleted })] }));
}
